import i18n from '@/core/locales/i18n'
import type { UeberfachlicheKompetenz } from './ueberfachliche-kompetenz'

export interface LessonImpulseOption {
  readonly key: string
  readonly label: string
}

export interface LessonImpulseLessonCounts {
  readonly running: number
  readonly finished: number
}

export interface LessonImpulseSummary {
  readonly id: string
  readonly bookmarkedAt: string | null
  readonly myLessonCounts: LessonImpulseLessonCounts
  readonly title: string
  readonly image: string | null
  readonly abstract: string | null
  readonly mission: string | null
  readonly subjects: ReadonlyArray<LessonImpulseCodeName>
  readonly kompetenzbereiche: ReadonlyArray<LessonImpulseCodeName>
  readonly isStandortbestimmung: boolean
  readonly impulseType: LessonImpulseOption
  readonly method: LessonImpulseOption
  readonly numberOfLessons: number
  readonly cycle: number
  readonly grades: ReadonlyArray<number>
  readonly gradeLabel: string | null
  readonly ueberfachlicheKompetenzen: ReadonlyArray<{
    readonly id: string
    readonly code: string
    readonly title: string
    readonly kompetenzbereich: { readonly key: string; readonly label: string }
  }>
}

export interface LessonImpulsePhase {
  readonly phaseId: string
  readonly number: string
  readonly title: string
  readonly durationMinutes: number
  readonly type:
    | 'einstieg'
    | 'kompetenzziel'
    | 'erarbeitung'
    | 'uebung'
    | 'anwendung'
    | 'ergebnissicherung'
    | 'reflexion'
    | 'transfer'
  readonly socialForm: LessonImpulseOption | null
  readonly level: LessonImpulseOption | null
  readonly description: string | null
  readonly goal: string | null
  readonly observationTip: string | null
}

export interface LessonImpulseMaterial {
  readonly materialId: string
  readonly name: string
  readonly phaseId: string
  readonly materialType: LessonImpulseOption
  readonly targetGroup: LessonImpulseOption
  readonly provision: LessonImpulseOption | null
  readonly purpose: string | null
  readonly file: string | null
  readonly content: string | null
  readonly levels: string | null
  readonly variants: string | null
}

export interface LessonImpulseVocabularyEntry {
  readonly term: string | null
  readonly explanation: string | null
  readonly exampleSentence: string | null
}

export interface LessonImpulseContext {
  readonly realWorldConnection: string | null
  readonly socialForm: LessonImpulseOption | null
  readonly languageSupport: ReadonlyArray<LessonImpulseVocabularyEntry>
  readonly differentiation: {
    readonly support: string | null
    readonly enrichment: string | null
  }
}

export interface LessonImpulseCodeName {
  readonly code: string
  readonly name: string
}

export interface LessonImpulseCodeText {
  readonly code: string
  readonly text: string
}

export interface LessonImpulseSubjectCompetence {
  readonly subject: LessonImpulseCodeName
  readonly kompetenzbereich: LessonImpulseCodeName
  readonly topicAspect?: LessonImpulseCodeName
  readonly competence: LessonImpulseCodeText
  readonly kompetenzstufe: {
    readonly code: string
    readonly cycles: ReadonlyArray<number>
    readonly basicRequirement: boolean
    readonly orientationPoint: boolean
    readonly laterInCycle: boolean
  }
  readonly kompetenzteilstufen: ReadonlyArray<LessonImpulseCodeText>
}

export interface UeberfachlichesZiel {
  readonly id: string
  readonly code: string
  readonly title: string
  readonly kompetenzbereich: {
    readonly key: string
    readonly label: string
  }
  readonly kompetenzstufen: ReadonlyArray<Kompetenzstufe>
}

export interface Kompetenzstufe {
  readonly id: string
  readonly code: string
  readonly text: string
  readonly indikatoren: ReadonlyArray<Indikator>
}

export interface Indikator {
  readonly id: string
  readonly teacherPhrasing: string
  readonly studentPhrasing: string
  readonly cycles: ReadonlyArray<number>
}

export interface Fach {
  readonly code: string
  readonly name: string
  readonly kompetenzbereiche: ReadonlyArray<FachKompetenzbereich>
}

export interface FachKompetenzbereich {
  readonly code: string
  readonly name: string
  readonly kompetenzen: ReadonlyArray<Fachkompetenz>
}

export interface Fachkompetenz {
  readonly code: string
  readonly text: string
  readonly kompetenzstufen: ReadonlyArray<FachKompetenzstufe>
}

export interface FachKompetenzstufe {
  readonly code: string
  readonly cycles: ReadonlyArray<number>
  readonly basicRequirement: boolean
  readonly orientationPoint: boolean
  readonly laterInCycle: boolean
  readonly kompetenzteilstufen: ReadonlyArray<Kompetenzteilstufe>
}

export interface Kompetenzteilstufe {
  readonly code: string
  readonly text: string
}

export interface LessonImpulseStandortbestimmung {
  readonly id: string
  readonly code: string
  readonly title: string
  readonly kompetenzbereich: {
    readonly key: string
    readonly label: string
  }
  readonly indikatoren: ReadonlyArray<Indikator>
}

export interface LessonImpulse {
  readonly id: string
  readonly bookmarkedAt: string | null
  readonly title: string
  readonly description: string | null
  readonly mission: string | null
  readonly subjectArea: string | null
  readonly subjects: ReadonlyArray<LessonImpulseCodeName>
  readonly kompetenzbereiche: ReadonlyArray<LessonImpulseCodeName>
  readonly impulseType: LessonImpulseOption
  readonly method: LessonImpulseOption
  readonly groupDynamics: LessonImpulseOption
  readonly numberOfLessons: number
  readonly cycle: number
  readonly image: string
  readonly grades: ReadonlyArray<number>
  readonly gradeLabel: string | null
  readonly teacherPrerequisites: string | null
  readonly studentPrerequisites: string | null
  readonly context: LessonImpulseContext
  readonly phases: ReadonlyArray<LessonImpulsePhase>
  readonly materials: ReadonlyArray<LessonImpulseMaterial>
  readonly subjectGoals: ReadonlyArray<Fach>
  readonly ueberfachlicheKompetenzen: ReadonlyArray<UeberfachlicheKompetenz>
  readonly ueberfachlicheZiele: ReadonlyArray<UeberfachlichesZiel>
  readonly reflexionQuestions: ReadonlyArray<{ id: string; text: string }>
  readonly standortbestimmung: ReadonlyArray<LessonImpulseStandortbestimmung>
  readonly myLessonCounts: LessonImpulseLessonCounts
}

export function gradeClassLabel(grades: ReadonlyArray<number>): string | null {
  const sorted = [...grades].sort((a, b) => a - b)
  const lowest = sorted[0]
  const highest = sorted[sorted.length - 1]
  if (lowest === undefined || highest === undefined) {
    return null
  }
  const classes = lowest === highest ? `${lowest}.` : `${lowest}.-${highest}.`
  return i18n.global.t('lumia.gradeLabels', { classes })
}

export function materialsForPhase(
  materials: ReadonlyArray<LessonImpulseMaterial>,
  phaseId: string,
): LessonImpulseMaterial[] {
  return materials.filter((material) => material.phaseId === phaseId)
}

export function unassignedMaterials(
  materials: ReadonlyArray<LessonImpulseMaterial>,
  phases: ReadonlyArray<LessonImpulsePhase>,
): LessonImpulseMaterial[] {
  const phaseIds = new Set(phases.map((phase) => phase.phaseId))
  return materials.filter((material) => !phaseIds.has(material.phaseId))
}
